import { Box, Chip, Tooltip, Typography, Divider } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { getFirebaseTimestampString } from "../../utils";

export default function AuditInfo({ data = {}, variant = "chip" }) {
  const { createdBy, updatedBy, createdAt, updatedAt } = data;

  const hasAny = createdBy || updatedBy;
  if (!hasAny) return null;

  if (variant === "chip") {
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
        {createdBy && (
          <Tooltip
            title={
              createdAt
                ? `Created at: ${getFirebaseTimestampString(createdAt)}`
                : "Created by"
            }
          >
            <Chip
              icon={<AddCircleOutlineIcon fontSize="small" />}
              label={`Created by: ${createdBy}`}
              size="small"
              variant="outlined"
              color="primary"
              sx={{ fontFamily: "monospace", fontSize: "0.72rem" }}
            />
          </Tooltip>
        )}
        {updatedBy && (
          <Tooltip
            title={
              updatedAt
                ? `Updated at: ${getFirebaseTimestampString(updatedAt)}`
                : "Updated by"
            }
          >
            <Chip
              icon={<EditOutlinedIcon fontSize="small" />}
              label={`Updated by: ${updatedBy}`}
              size="small"
              variant="outlined"
              color="secondary"
              sx={{ fontFamily: "monospace", fontSize: "0.72rem" }}
            />
          </Tooltip>
        )}
      </Box>
    );
  }

  // ── INLINE variant ────────────────────────────────────────────────────────
  if (variant === "inline") {
    return (
      <Box
        sx={{
          display: "flex",
          gap: 3,
          flexWrap: "wrap",
          color: "text.secondary",
          fontSize: "0.8rem",
        }}
      >
        {createdBy && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <AddCircleOutlineIcon sx={{ fontSize: 15 }} />
            <Typography variant="caption">
              Created by <strong>{createdBy}</strong>
              {createdAt && ` · ${getFirebaseTimestampString(createdAt)}`}
            </Typography>
          </Box>
        )}
        {updatedBy && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <EditOutlinedIcon sx={{ fontSize: 15 }} />
            <Typography variant="caption">
              Updated by <strong>{updatedBy}</strong>
              {updatedAt && ` · ${getFirebaseTimestampString(updatedAt)}`}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // ── CARD variant ──────────────────────────────────────────────────────────
  if (variant === "card") {
    return (
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 4,
          p: 2,
          bgcolor: "grey.50",
          overflowWrap: "anywhere",
          "& .MuiBox-root": { minWidth: 0 },
          "& .MuiSvgIcon-root": { flexShrink: 0 },
          mt: 2,
        }}
      >
        <Typography
          variant="overline"
          sx={{
            color: "text.secondary",
            letterSpacing: 1.5,
            display: "block",
            mb: 1,
          }}
        >
          Audit Info
        </Typography>
        <Divider sx={{ mb: 1.5 }} />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {createdBy && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AddCircleOutlineIcon color="primary" fontSize="small" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Created by
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    overflowWrap: "anywhere",
                  }}
                >
                  {createdBy}
                </Typography>
                {createdAt && (
                  <Typography variant="caption" color="text.secondary">
                    {getFirebaseTimestampString(createdAt)}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          {updatedBy && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EditOutlinedIcon color="secondary" fontSize="small" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Updated by
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    overflowWrap: "anywhere",
                  }}
                >
                  {updatedBy}
                </Typography>
                {updatedAt && (
                  <Typography variant="caption" color="text.secondary">
                    {getFirebaseTimestampString(updatedAt)}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return null;
}